import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import myAxios from "../AxiosUrl";
import "./Settings.css";
import Loading from "../Loading/Loading";

function Settings() {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    site: {
      name: "",
      description: "",
      maintenance_mode: false,
      max_users: 10000,
      environment: "development",
    },
    payment: {
      stripe: {
        secret_key: "",
        publishable_key: "",
        webhook_secret: "",
        enabled: true,
      },
      apple: {
        shared_secret: "",
        bundle_id: "",
        team_id: "",
        enabled: true,
      },
      google_play: {
        package_name: "",
        service_account_key: "",
        enabled: true,
      },
      default_currency: "USD",
      trial_period_days: 14,
    },
    email: {
      smtp: {
        host: "",
        port: 587,
        secure: false,
        username: "",
        password: "",
      },
      from_name: "",
      from_address: "",
      notifications_enabled: true,
    },
    security: {
      password_min_length: 6,
      login_rate_limit: 5,
      jwt_expiry: "24h",
      super_admin_emails: [],
      session_timeout: 3600,
    },
    upload: {
      max_file_size: 10485760,
      allowed_types: [],
      image_quality: 80,
      max_files_per_request: 10,
      storage_path: "uploads/",
    },
    features: {
      websocket_enabled: true,
      google_play_billing: true,
      apple_store_billing: true,
      email_notifications: true,
      maintenance_mode: false,
      new_user_registration: true,
    },
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await myAxios.get("/settings");

      if (response.data.success) {
        const adminSettings = response.data.data;
        // Convert individual settings to nested structure
        const convertedSettings =
          convertSettingsToNestedStructure(adminSettings);
        setSettings(convertedSettings);
      }
    } catch (error) {
      toast.error("Failed to fetch settings");
      console.error("Settings fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const convertSettingsToNestedStructure = (adminSettings) => {
    const converted = {
      site: {
        name: "",
        description: "",
        maintenance_mode: false,
        max_users: 10000,
        environment: "development",
      },
      payment: {
        stripe: {
          secret_key: "",
          publishable_key: "",
          webhook_secret: "",
          enabled: true,
        },
        apple: {
          shared_secret: "",
          bundle_id: "",
          team_id: "",
          enabled: true,
        },
        google_play: {
          package_name: "",
          service_account_key: "",
          enabled: true,
        },
        default_currency: "USD",
        trial_period_days: 14,
      },
      email: {
        smtp: {
          host: "",
          port: 587,
          secure: false,
          username: "",
          password: "",
        },
        from_name: "",
        from_address: "",
        notifications_enabled: true,
      },
      security: {
        password_min_length: 6,
        login_rate_limit: 5,
        jwt_expiry: "24h",
        super_admin_emails: [],
        session_timeout: 3600,
      },
      upload: {
        max_file_size: 10485760,
        allowed_types: [],
        image_quality: 80,
        max_files_per_request: 10,
        storage_path: "uploads/",
      },
      features: {
        websocket_enabled: true,
        google_play_billing: true,
        apple_store_billing: true,
        email_notifications: true,
        maintenance_mode: false,
        new_user_registration: true,
      },
    };

    // Map individual settings to nested structure
    adminSettings.forEach((setting) => {
      const key = setting.key;
      const value = setting.value;

      // Site settings
      if (key === "site_name") converted.site.name = value;
      if (key === "site_description") converted.site.description = value;
      if (key === "site_maintenance_mode")
        converted.site.maintenance_mode = value;
      if (key === "site_max_users") converted.site.max_users = value;
      if (key === "site_environment") converted.site.environment = value;

      // Payment settings - Stripe
      if (key === "stripe_secret_key")
        converted.payment.stripe.secret_key = value;
      if (key === "stripe_publishable_key")
        converted.payment.stripe.publishable_key = value;
      if (key === "stripe_webhook_secret")
        converted.payment.stripe.webhook_secret = value;
      if (key === "stripe_enabled") converted.payment.stripe.enabled = value;

      // Payment settings - Apple
      if (key === "apple_shared_secret")
        converted.payment.apple.shared_secret = value;
      if (key === "apple_bundle_id") converted.payment.apple.bundle_id = value;
      if (key === "apple_team_id") converted.payment.apple.team_id = value;
      if (key === "apple_enabled") converted.payment.apple.enabled = value;

      // Payment settings - Google Play
      if (key === "google_play_package_name")
        converted.payment.google_play.package_name = value;
      if (key === "google_play_service_account_key")
        converted.payment.google_play.service_account_key = value;
      if (key === "google_play_enabled")
        converted.payment.google_play.enabled = value;

      // Payment settings - General
      if (key === "payment_default_currency")
        converted.payment.default_currency = value;
      if (key === "payment_trial_period_days")
        converted.payment.trial_period_days = value;

      // Email settings
      if (key === "email_smtp_host") converted.email.smtp.host = value;
      if (key === "email_smtp_port") converted.email.smtp.port = value;
      if (key === "email_smtp_secure") converted.email.smtp.secure = value;
      if (key === "email_smtp_username") converted.email.smtp.username = value;
      if (key === "email_smtp_password") converted.email.smtp.password = value;
      if (key === "email_from_name") converted.email.from_name = value;
      if (key === "email_from_address") converted.email.from_address = value;
      if (key === "email_notifications_enabled")
        converted.email.notifications_enabled = value;

      // Security settings
      if (key === "security_password_min_length")
        converted.security.password_min_length = value;
      if (key === "security_login_rate_limit")
        converted.security.login_rate_limit = value;
      if (key === "security_jwt_expiry") converted.security.jwt_expiry = value;
      if (key === "security_super_admin_emails")
        converted.security.super_admin_emails = value;
      if (key === "security_session_timeout")
        converted.security.session_timeout = value;

      // Upload settings
      if (key === "upload_max_file_size")
        converted.upload.max_file_size = value;
      if (key === "upload_allowed_types")
        converted.upload.allowed_types = value;
      if (key === "upload_image_quality")
        converted.upload.image_quality = value;
      if (key === "upload_max_files_per_request")
        converted.upload.max_files_per_request = value;
      if (key === "upload_storage_path") converted.upload.storage_path = value;

      // Features settings
      if (key === "features_websocket_enabled")
        converted.features.websocket_enabled = value;
      if (key === "features_google_play_billing")
        converted.features.google_play_billing = value;
      if (key === "features_apple_store_billing")
        converted.features.apple_store_billing = value;
      if (key === "features_email_notifications")
        converted.features.email_notifications = value;
      if (key === "features_maintenance_mode")
        converted.features.maintenance_mode = value;
      if (key === "features_new_user_registration")
        converted.features.new_user_registration = value;
    });

    return converted;
  };

  const handleSettingChange = (category, subcategory, field, value) => {
    if (subcategory) {
      setSettings((prev) => ({
        ...prev,
        [category]: {
          ...prev[category],
          [subcategory]: {
            ...(prev[category]?.[subcategory] || {}),
            [field]: value,
          },
        },
      }));
      return;
    }

    setSettings((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
  };

  const handleArrayChange = (category, field, value) => {
    setSettings((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value.split(",").map((item) => item.trim()),
      },
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Convert nested structure back to individual settings
      const settingsToUpdate = [
        // Site settings
        { key: "site_name", value: settings.site.name },
        { key: "site_description", value: settings.site.description },
        { key: "site_maintenance_mode", value: settings.site.maintenance_mode },
        { key: "site_max_users", value: settings.site.max_users },
        { key: "site_environment", value: settings.site.environment },

        // Payment settings - Stripe
        { key: "stripe_secret_key", value: settings.payment.stripe.secret_key },
        {
          key: "stripe_publishable_key",
          value: settings.payment.stripe.publishable_key,
        },
        {
          key: "stripe_webhook_secret",
          value: settings.payment.stripe.webhook_secret,
        },
        { key: "stripe_enabled", value: settings.payment.stripe.enabled },

        // Payment settings - Apple
        {
          key: "apple_shared_secret",
          value: settings.payment.apple.shared_secret,
        },
        { key: "apple_bundle_id", value: settings.payment.apple.bundle_id },
        { key: "apple_team_id", value: settings.payment.apple.team_id },
        { key: "apple_enabled", value: settings.payment.apple.enabled },

        // Payment settings - Google Play
        {
          key: "google_play_package_name",
          value: settings.payment.google_play.package_name,
        },
        {
          key: "google_play_service_account_key",
          value: settings.payment.google_play.service_account_key,
        },
        {
          key: "google_play_enabled",
          value: settings.payment.google_play.enabled,
        },

        // Payment settings - General
        {
          key: "payment_default_currency",
          value: settings.payment.default_currency,
        },
        {
          key: "payment_trial_period_days",
          value: settings.payment.trial_period_days,
        },

        // Email settings
        { key: "email_smtp_host", value: settings.email.smtp.host },
        { key: "email_smtp_port", value: settings.email.smtp.port },
        { key: "email_smtp_secure", value: settings.email.smtp.secure },
        { key: "email_smtp_username", value: settings.email.smtp.username },
        { key: "email_smtp_password", value: settings.email.smtp.password },
        { key: "email_from_name", value: settings.email.from_name },
        { key: "email_from_address", value: settings.email.from_address },
        {
          key: "email_notifications_enabled",
          value: settings.email.notifications_enabled,
        },

        // Security settings
        {
          key: "security_password_min_length",
          value: settings.security.password_min_length,
        },
        {
          key: "security_login_rate_limit",
          value: settings.security.login_rate_limit,
        },
        { key: "security_jwt_expiry", value: settings.security.jwt_expiry },
        {
          key: "security_super_admin_emails",
          value: settings.security.super_admin_emails,
        },
        {
          key: "security_session_timeout",
          value: settings.security.session_timeout,
        },

        // Upload settings
        { key: "upload_max_file_size", value: settings.upload.max_file_size },
        { key: "upload_allowed_types", value: settings.upload.allowed_types },
        { key: "upload_image_quality", value: settings.upload.image_quality },
        {
          key: "upload_max_files_per_request",
          value: settings.upload.max_files_per_request,
        },
        { key: "upload_storage_path", value: settings.upload.storage_path },

        // Features settings
        {
          key: "features_websocket_enabled",
          value: settings.features.websocket_enabled,
        },
        {
          key: "features_google_play_billing",
          value: settings.features.google_play_billing,
        },
        {
          key: "features_apple_store_billing",
          value: settings.features.apple_store_billing,
        },
        {
          key: "features_email_notifications",
          value: settings.features.email_notifications,
        },
        {
          key: "features_maintenance_mode",
          value: settings.features.maintenance_mode,
        },
        {
          key: "features_new_user_registration",
          value: settings.features.new_user_registration,
        },
      ];

      // Update each setting individually
      const promises = settingsToUpdate.map(async (setting) => {
        try {
          await myAxios.put(`/settings/key/${setting.key}`, {
            value: setting.value,
          });
        } catch (error) {
          console.error(`Failed to update setting ${setting.key}:`, error);
          throw error;
        }
      });

      await Promise.all(promises);
      toast.success("Settings saved successfully!");
    } catch (error) {
      toast.error("Failed to save settings");
      console.error("Settings save error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <div className="settings-icon">
          <span className="material-icons">settings</span>
        </div>
        <h2>Admin Settings</h2>
      </div>

      <div className="settings-content">
        {/* Site Settings Section */}
        <div className="settings-section">
          <div className="section-header">
            <span className="material-icons">web</span>
            <h3>Site Settings</h3>
          </div>
          <div className="section-content">
            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">label</span>
                <div>
                  <h4>Site Name</h4>
                  <p>Your site name</p>
                </div>
              </div>
              <input
                type="text"
                className="api-input"
                value={settings.site.name}
                onChange={(e) =>
                  handleSettingChange("site", null, "name", e.target.value)
                }
                placeholder="Site Name"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">description</span>
                <div>
                  <h4>Description</h4>
                  <p>Site description</p>
                </div>
              </div>
              <textarea
                className="api-input"
                value={settings.site.description}
                onChange={(e) =>
                  handleSettingChange(
                    "site",
                    null,
                    "description",
                    e.target.value
                  )
                }
                placeholder="Site Description"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">engineering</span>
                <div>
                  <h4>Maintenance Mode</h4>
                  <p>Enable/disable maintenance mode</p>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.site.maintenance_mode}
                  onChange={(e) =>
                    handleSettingChange(
                      "site",
                      null,
                      "maintenance_mode",
                      e.target.checked
                    )
                  }
                />
                <span className="slider round"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">group</span>
                <div>
                  <h4>Max Users</h4>
                  <p>Maximum number of users allowed</p>
                </div>
              </div>
              <input
                type="number"
                className="api-input"
                value={settings.site.max_users}
                onChange={(e) =>
                  handleSettingChange(
                    "site",
                    null,
                    "max_users",
                    parseInt(e.target.value)
                  )
                }
                min="1"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">code</span>
                <div>
                  <h4>Environment</h4>
                  <p>Application environment</p>
                </div>
              </div>
              <select
                className="api-input"
                value={settings.site.environment}
                onChange={(e) =>
                  handleSettingChange(
                    "site",
                    null,
                    "environment",
                    e.target.value
                  )
                }
              >
                <option value="development">Development</option>
                <option value="staging">Staging</option>
                <option value="production">Production</option>
              </select>
            </div>
          </div>
        </div>

        {/* Payment Settings Section */}
        <div className="settings-section">
          <div className="section-header">
            <span className="material-icons">payments</span>
            <h3>Payment Settings</h3>
          </div>
          <div className="section-content">
            {/* Stripe Settings */}
            <div className="subsection">
              <h4>Stripe Configuration</h4>
              <div className="setting-item">
                <div className="setting-info">
                  <span className="material-icons">key</span>
                  <div>
                    <h4>Secret Key</h4>
                    <p>Stripe secret key</p>
                  </div>
                </div>
                <input
                  type="password"
                  className="api-input"
                  value={settings.payment.stripe.secret_key}
                  onChange={(e) =>
                    handleSettingChange(
                      "payment",
                      "stripe",
                      "secret_key",
                      e.target.value
                    )
                  }
                  placeholder="sk_test_..."
                />
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <span className="material-icons">key</span>
                  <div>
                    <h4>Publishable Key</h4>
                    <p>Stripe publishable key</p>
                  </div>
                </div>
                <input
                  type="text"
                  className="api-input"
                  value={settings.payment.stripe.publishable_key}
                  onChange={(e) =>
                    handleSettingChange(
                      "payment",
                      "stripe",
                      "publishable_key",
                      e.target.value
                    )
                  }
                  placeholder="pk_test_..."
                />
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <span className="material-icons">webhook</span>
                  <div>
                    <h4>Webhook Secret</h4>
                    <p>Stripe webhook endpoint secret</p>
                  </div>
                </div>
                <input
                  type="password"
                  className="api-input"
                  value={settings.payment.stripe.webhook_secret}
                  onChange={(e) =>
                    handleSettingChange(
                      "payment",
                      "stripe",
                      "webhook_secret",
                      e.target.value
                    )
                  }
                  placeholder="whsec_..."
                />
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <span className="material-icons">toggle_on</span>
                  <div>
                    <h4>Stripe Enabled</h4>
                    <p>Enable/disable Stripe payments</p>
                  </div>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={settings.payment.stripe.enabled}
                    onChange={(e) =>
                      handleSettingChange(
                        "payment",
                        "stripe",
                        "enabled",
                        e.target.checked
                      )
                    }
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            {/* Apple Settings */}
            <div className="subsection">
              <h4>Apple App Store Configuration</h4>
              <div className="setting-item">
                <div className="setting-info">
                  <span className="material-icons">key</span>
                  <div>
                    <h4>Shared Secret</h4>
                    <p>Apple App Store shared secret</p>
                  </div>
                </div>
                <input
                  type="password"
                  className="api-input"
                  value={settings.payment.apple.shared_secret}
                  onChange={(e) =>
                    handleSettingChange(
                      "payment",
                      "apple",
                      "shared_secret",
                      e.target.value
                    )
                  }
                  placeholder="Shared Secret"
                />
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <span className="material-icons">apps</span>
                  <div>
                    <h4>Bundle ID</h4>
                    <p>Apple App Store bundle identifier</p>
                  </div>
                </div>
                <input
                  type="text"
                  className="api-input"
                  value={settings.payment.apple.bundle_id}
                  onChange={(e) =>
                    handleSettingChange(
                      "payment",
                      "apple",
                      "bundle_id",
                      e.target.value
                    )
                  }
                  placeholder="com.yourcompany.app"
                />
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <span className="material-icons">toggle_on</span>
                  <div>
                    <h4>Apple Store Enabled</h4>
                    <p>Enable/disable Apple App Store payments</p>
                  </div>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={settings.payment.apple.enabled}
                    onChange={(e) =>
                      handleSettingChange(
                        "payment",
                        "apple",
                        "enabled",
                        e.target.checked
                      )
                    }
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            {/* Google Play Settings */}
            <div className="subsection">
              <h4>Google Play Configuration</h4>
              <div className="setting-item">
                <div className="setting-info">
                  <span className="material-icons">android</span>
                  <div>
                    <h4>Package Name</h4>
                    <p>Google Play package name</p>
                  </div>
                </div>
                <input
                  type="text"
                  className="api-input"
                  value={settings.payment.google_play.package_name}
                  onChange={(e) =>
                    handleSettingChange(
                      "payment",
                      "google_play",
                      "package_name",
                      e.target.value
                    )
                  }
                  placeholder="com.yourapp.package"
                />
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <span className="material-icons">key</span>
                  <div>
                    <h4>Service Account Key</h4>
                    <p>Path to Google service account JSON file</p>
                  </div>
                </div>
                <input
                  type="text"
                  className="api-input"
                  value={settings.payment.google_play.service_account_key}
                  onChange={(e) =>
                    handleSettingChange(
                      "payment",
                      "google_play",
                      "service_account_key",
                      e.target.value
                    )
                  }
                  placeholder="./path/to/service-account.json"
                />
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <span className="material-icons">toggle_on</span>
                  <div>
                    <h4>Google Play Enabled</h4>
                    <p>Enable/disable Google Play payments</p>
                  </div>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={settings.payment.google_play.enabled}
                    onChange={(e) =>
                      handleSettingChange(
                        "payment",
                        "google_play",
                        "enabled",
                        e.target.checked
                      )
                    }
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            {/* General Payment Settings */}
            <div className="subsection">
              <h4>General Payment Settings</h4>
              <div className="setting-item">
                <div className="setting-info">
                  <span className="material-icons">money</span>
                  <div>
                    <h4>Default Currency</h4>
                    <p>Default currency for payments</p>
                  </div>
                </div>
                <select
                  className="api-input"
                  value={settings.payment.default_currency}
                  onChange={(e) =>
                    handleSettingChange(
                      "payment",
                      null,
                      "default_currency",
                      e.target.value
                    )
                  }
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <span className="material-icons">schedule</span>
                  <div>
                    <h4>Trial Period Days</h4>
                    <p>Default trial period in days</p>
                  </div>
                </div>
                <input
                  type="number"
                  className="api-input"
                  value={settings.payment.trial_period_days}
                  onChange={(e) =>
                    handleSettingChange(
                      "payment",
                      null,
                      "trial_period_days",
                      parseInt(e.target.value)
                    )
                  }
                  min="0"
                  max="90"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Email Settings Section */}
        <div className="settings-section">
          <div className="section-header">
            <span className="material-icons">email</span>
            <h3>Email Settings</h3>
          </div>
          <div className="section-content">
            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">dns</span>
                <div>
                  <h4>SMTP Host</h4>
                  <p>SMTP server host</p>
                </div>
              </div>
              <input
                type="text"
                className="api-input"
                value={settings.email.smtp.host}
                onChange={(e) =>
                  handleSettingChange("email", "smtp", "host", e.target.value)
                }
                placeholder="smtp.gmail.com"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">pin</span>
                <div>
                  <h4>SMTP Port</h4>
                  <p>SMTP server port</p>
                </div>
              </div>
              <input
                type="number"
                className="api-input"
                value={settings.email.smtp.port}
                onChange={(e) =>
                  handleSettingChange(
                    "email",
                    "smtp",
                    "port",
                    parseInt(e.target.value)
                  )
                }
                min="1"
                max="65535"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">security</span>
                <div>
                  <h4>Secure Connection</h4>
                  <p>Use SSL/TLS for SMTP</p>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.email.smtp.secure}
                  onChange={(e) =>
                    handleSettingChange(
                      "email",
                      "smtp",
                      "secure",
                      e.target.checked
                    )
                  }
                />
                <span className="slider round"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">person</span>
                <div>
                  <h4>SMTP Username</h4>
                  <p>SMTP authentication username</p>
                </div>
              </div>
              <input
                type="text"
                className="api-input"
                value={settings.email.smtp.username}
                onChange={(e) =>
                  handleSettingChange(
                    "email",
                    "smtp",
                    "username",
                    e.target.value
                  )
                }
                placeholder="username@gmail.com"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">lock</span>
                <div>
                  <h4>SMTP Password</h4>
                  <p>SMTP authentication password</p>
                </div>
              </div>
              <input
                type="password"
                className="api-input"
                value={settings.email.smtp.password}
                onChange={(e) =>
                  handleSettingChange(
                    "email",
                    "smtp",
                    "password",
                    e.target.value
                  )
                }
                placeholder="App Password"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">badge</span>
                <div>
                  <h4>From Name</h4>
                  <p>Default sender name</p>
                </div>
              </div>
              <input
                type="text"
                className="api-input"
                value={settings.email.from_name}
                onChange={(e) =>
                  handleSettingChange(
                    "email",
                    null,
                    "from_name",
                    e.target.value
                  )
                }
                placeholder="Your App Name"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">alternate_email</span>
                <div>
                  <h4>From Address</h4>
                  <p>Default sender email address</p>
                </div>
              </div>
              <input
                type="email"
                className="api-input"
                value={settings.email.from_address}
                onChange={(e) =>
                  handleSettingChange(
                    "email",
                    null,
                    "from_address",
                    e.target.value
                  )
                }
                placeholder="noreply@yourapp.com"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">notifications</span>
                <div>
                  <h4>Email Notifications</h4>
                  <p>Enable/disable email notifications</p>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.email.notifications_enabled}
                  onChange={(e) =>
                    handleSettingChange(
                      "email",
                      null,
                      "notifications_enabled",
                      e.target.checked
                    )
                  }
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Security Settings Section */}
        <div className="settings-section">
          <div className="section-header">
            <span className="material-icons">security</span>
            <h3>Security Settings</h3>
          </div>
          <div className="section-content">
            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">password</span>
                <div>
                  <h4>Password Min Length</h4>
                  <p>Minimum password length</p>
                </div>
              </div>
              <input
                type="number"
                className="api-input"
                value={settings.security.password_min_length}
                onChange={(e) =>
                  handleSettingChange(
                    "security",
                    null,
                    "password_min_length",
                    parseInt(e.target.value)
                  )
                }
                min="6"
                max="32"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">speed</span>
                <div>
                  <h4>Login Rate Limit</h4>
                  <p>Maximum login attempts per minute</p>
                </div>
              </div>
              <input
                type="number"
                className="api-input"
                value={settings.security.login_rate_limit}
                onChange={(e) =>
                  handleSettingChange(
                    "security",
                    null,
                    "login_rate_limit",
                    parseInt(e.target.value)
                  )
                }
                min="1"
                max="100"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">timer</span>
                <div>
                  <h4>JWT Expiry</h4>
                  <p>JWT token expiration time</p>
                </div>
              </div>
              <input
                type="text"
                className="api-input"
                value={settings.security.jwt_expiry}
                onChange={(e) =>
                  handleSettingChange(
                    "security",
                    null,
                    "jwt_expiry",
                    e.target.value
                  )
                }
                placeholder="24h"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">admin_panel_settings</span>
                <div>
                  <h4>Super Admin Emails</h4>
                  <p>Comma-separated list of super admin emails</p>
                </div>
              </div>
              <input
                type="text"
                className="api-input"
                value={settings.security.super_admin_emails?.join(", ") || ""}
                onChange={(e) =>
                  handleArrayChange(
                    "security",
                    "super_admin_emails",
                    e.target.value
                  )
                }
                placeholder="admin1@example.com, admin2@example.com"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">schedule</span>
                <div>
                  <h4>Session Timeout</h4>
                  <p>Session timeout in seconds</p>
                </div>
              </div>
              <input
                type="number"
                className="api-input"
                value={settings.security.session_timeout}
                onChange={(e) =>
                  handleSettingChange(
                    "security",
                    null,
                    "session_timeout",
                    parseInt(e.target.value)
                  )
                }
                min="300"
                max="86400"
              />
            </div>
          </div>
        </div>

        {/* Upload Settings Section */}
        <div className="settings-section">
          <div className="section-header">
            <span className="material-icons">upload_file</span>
            <h3>Upload Settings</h3>
          </div>
          <div className="section-content">
            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">storage</span>
                <div>
                  <h4>Max File Size</h4>
                  <p>Maximum file size in bytes</p>
                </div>
              </div>
              <input
                type="number"
                className="api-input"
                value={settings.upload.max_file_size}
                onChange={(e) =>
                  handleSettingChange(
                    "upload",
                    null,
                    "max_file_size",
                    parseInt(e.target.value)
                  )
                }
                min="1024"
                max="104857600"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">category</span>
                <div>
                  <h4>Allowed Types</h4>
                  <p>Comma-separated list of allowed file types</p>
                </div>
              </div>
              <input
                type="text"
                className="api-input"
                value={settings.upload.allowed_types?.join(", ") || ""}
                onChange={(e) =>
                  handleArrayChange("upload", "allowed_types", e.target.value)
                }
                placeholder="jpg, png, pdf"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">photo</span>
                <div>
                  <h4>Image Quality</h4>
                  <p>Compression quality for images (1-100)</p>
                </div>
              </div>
              <input
                type="number"
                className="api-input"
                value={settings.upload.image_quality}
                onChange={(e) =>
                  handleSettingChange(
                    "upload",
                    null,
                    "image_quality",
                    parseInt(e.target.value)
                  )
                }
                min="1"
                max="100"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">file_copy</span>
                <div>
                  <h4>Max Files per Request</h4>
                  <p>Maximum number of files per upload request</p>
                </div>
              </div>
              <input
                type="number"
                className="api-input"
                value={settings.upload.max_files_per_request}
                onChange={(e) =>
                  handleSettingChange(
                    "upload",
                    null,
                    "max_files_per_request",
                    parseInt(e.target.value)
                  )
                }
                min="1"
                max="50"
              />
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">folder</span>
                <div>
                  <h4>Storage Path</h4>
                  <p>Default storage path for uploads</p>
                </div>
              </div>
              <input
                type="text"
                className="api-input"
                value={settings.upload.storage_path}
                onChange={(e) =>
                  handleSettingChange(
                    "upload",
                    null,
                    "storage_path",
                    e.target.value
                  )
                }
                placeholder="uploads/"
              />
            </div>
          </div>
        </div>

        {/* Features Settings Section */}
        <div className="settings-section">
          <div className="section-header">
            <span className="material-icons">extension</span>
            <h3>Features Settings</h3>
          </div>
          <div className="section-content">
            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">sync</span>
                <div>
                  <h4>WebSocket Enabled</h4>
                  <p>Enable/disable real-time features</p>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.features.websocket_enabled}
                  onChange={(e) =>
                    handleSettingChange(
                      "features",
                      null,
                      "websocket_enabled",
                      e.target.checked
                    )
                  }
                />
                <span className="slider round"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">android</span>
                <div>
                  <h4>Google Play Billing</h4>
                  <p>Enable/disable Google Play payments</p>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.features.google_play_billing}
                  onChange={(e) =>
                    handleSettingChange(
                      "features",
                      null,
                      "google_play_billing",
                      e.target.checked
                    )
                  }
                />
                <span className="slider round"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">phone_iphone</span>
                <div>
                  <h4>Apple Store Billing</h4>
                  <p>Enable/disable Apple App Store payments</p>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.features.apple_store_billing}
                  onChange={(e) =>
                    handleSettingChange(
                      "features",
                      null,
                      "apple_store_billing",
                      e.target.checked
                    )
                  }
                />
                <span className="slider round"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">email</span>
                <div>
                  <h4>Email Notifications</h4>
                  <p>Enable/disable email notifications</p>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.features.email_notifications}
                  onChange={(e) =>
                    handleSettingChange(
                      "features",
                      null,
                      "email_notifications",
                      e.target.checked
                    )
                  }
                />
                <span className="slider round"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">engineering</span>
                <div>
                  <h4>Maintenance Mode</h4>
                  <p>Enable/disable maintenance mode</p>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.features.maintenance_mode}
                  onChange={(e) =>
                    handleSettingChange(
                      "features",
                      null,
                      "maintenance_mode",
                      e.target.checked
                    )
                  }
                />
                <span className="slider round"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <span className="material-icons">person_add</span>
                <div>
                  <h4>New User Registration</h4>
                  <p>Enable/disable new user registration</p>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.features.new_user_registration}
                  onChange={(e) =>
                    handleSettingChange(
                      "features",
                      null,
                      "new_user_registration",
                      e.target.checked
                    )
                  }
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-actions">
        <button className="save-btn" onClick={handleSave} disabled={loading}>
          {loading ? (
            <Loading />
          ) : (
            <>
              <span className="material-icons">save</span>
              Save Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default Settings;
